import React, { useState, useEffect } from 'react';
import { fetchFromDentalAPI } from './common';

function fmt(cents) {
  return `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtDate(iso) {
  if (!iso) return '\u2014';
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function pct(rate) {
  return `${(rate * 100).toFixed(0)}%`;
}

const statusColors = {
  active: { bg: '#dcfce7', color: '#166534' },
  trialing: { bg: '#dbeafe', color: '#1e40af' },
  canceled: { bg: '#fee2e2', color: '#991b1b' },
  past_due: { bg: '#fef9c3', color: '#854d0e' },
};

const stageColors = {
  Lead: { bg: '#f1f5f9', color: '#334155' },
  Opportunity: { bg: '#dbeafe', color: '#1e40af' },
};

const typeColors = {
  'New Business': { bg: '#ecfdf5', color: '#047857' },
  'Expansion': { bg: '#f5f3ff', color: '#6d28d9' },
  'Upsell': { bg: '#fffbeb', color: '#b45309' },
};

function Badge({ label, colorMap }) {
  const c = colorMap[label] || colorMap[label?.toLowerCase?.()] || { bg: '#f3f4f6', color: '#374151' };
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center',
      padding: '2px 8px', borderRadius: '9999px',
      fontSize: '12px', fontWeight: 500,
      backgroundColor: c.bg, color: c.color,
    }}>
      {label}
    </span>
  );
}

function RateBadge({ rate }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center',
      padding: '2px 8px', borderRadius: '9999px',
      fontSize: '12px', fontWeight: 500,
      backgroundColor: '#ecfdf5', color: '#047857',
    }}>
      {pct(rate)} rev share
    </span>
  );
}

function ProgressBar({ current, total }) {
  const p = Math.min((current / total) * 100, 100);
  return (
    <div style={{ width: '100%', backgroundColor: '#e5e7eb', borderRadius: '9999px', height: '8px' }}>
      <div style={{
        width: `${p}%`, backgroundColor: '#059669',
        height: '8px', borderRadius: '9999px', transition: 'width 0.3s',
      }} />
    </div>
  );
}

const journeyColors = {
  paid_out: { bg: '#ecfdf5', border: '#6ee7b7' },
  payable: { bg: '#fffbeb', border: '#fcd34d' },
  future: { bg: '#f9fafb', border: '#e5e7eb' },
};

const journeyCommissionColors = {
  paid_out: '#047857',
  payable: '#b45309',
};

function JourneyTimeline({ journey }) {
  return (
    <div style={{ marginTop: '12px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '8px' }}>
        <p style={{ margin: 0, fontSize: '11px', fontWeight: 500, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Payout Journey</p>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', fontSize: '10px', color: '#9ca3af' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <span style={{ display: 'inline-block', width: '8px', height: '8px', borderRadius: '2px', backgroundColor: '#a7f3d0', border: '1px solid #6ee7b7' }} /> Paid out
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <span style={{ display: 'inline-block', width: '8px', height: '8px', borderRadius: '2px', backgroundColor: '#fef3c7', border: '1px solid #fcd34d' }} /> Payable
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <span style={{ display: 'inline-block', width: '8px', height: '8px', borderRadius: '2px', backgroundColor: '#f3f4f6', border: '1px solid #e5e7eb' }} /> Future
          </span>
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: '4px' }}>
        {journey.map((m) => {
          const jc = journeyColors[m.status];
          return (
            <div
              key={m.monthNumber}
              style={{
                borderRadius: '8px', padding: '8px 4px', textAlign: 'center',
                border: `1px solid ${jc.border}`, backgroundColor: jc.bg,
              }}
            >
              <p style={{ margin: 0, fontSize: '10px', fontWeight: 500, color: '#9ca3af' }}>M{m.monthNumber}</p>
              {m.status !== 'future' ? (
                <>
                  <p style={{ margin: '2px 0 0', fontSize: '11px', fontWeight: 600, color: '#111827' }}>{fmt(m.invoiceAmount)}</p>
                  <p style={{ margin: '1px 0 0', fontSize: '10px', color: journeyCommissionColors[m.status] }}>{fmt(m.commission)}</p>
                  {m.payoutDate && (
                    <p style={{ margin: '2px 0 0', fontSize: '9px', color: '#9ca3af' }}>
                      {m.status === 'paid_out' ? 'Paid' : 'Due'} {fmtDate(m.payoutDate)}
                    </p>
                  )}
                </>
              ) : (
                <p style={{ margin: '2px 0 0', fontSize: '11px', color: '#9ca3af' }}>&mdash;</p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function InFlightSection({ deals, showRecipient = false }) {
  if (!deals || deals.length === 0) return null;

  const opportunities = deals.filter((d) => d.stage === 'Opportunity');
  const leads = deals.filter((d) => d.stage === 'Lead');
  const other = deals.filter((d) => d.stage !== 'Lead' && d.stage !== 'Opportunity');
  const grouped = [...opportunities, ...leads, ...other];

  return (
    <div style={{ marginTop: '32px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
        <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600 }}>In-Flight Deals</h3>
        <span style={{
          display: 'inline-flex', alignItems: 'center', padding: '2px 8px',
          borderRadius: '9999px', fontSize: '12px', fontWeight: 500,
          backgroundColor: '#f3f4f6', color: '#4b5563',
        }}>
          {deals.length}
        </span>
      </div>
      <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: '12px', overflow: 'hidden' }}>
        {grouped.map((deal) => (
          <div
            key={deal.dealRecordId}
            style={{
              padding: '14px 24px',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              borderBottom: '1px solid #f3f4f6',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0 }}>
              <Badge label={deal.stage} colorMap={stageColors} />
              <div style={{ minWidth: 0 }}>
                <p style={{ margin: 0, fontSize: '14px', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{deal.dealName}</p>
                <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{deal.companyName || 'No company'}</p>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexShrink: 0 }}>
              {deal.type && <Badge label={deal.type} colorMap={typeColors} />}
              {deal.locations > 0 && (
                <span style={{ fontSize: '12px', color: '#6b7280' }}>
                  {deal.locations} loc{deal.locations !== 1 ? 's' : ''}
                </span>
              )}
              {showRecipient && (
                <span style={{ fontSize: '12px', color: '#6b7280' }}>{deal.payoutRecipient}</span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const PayoutsTab = () => {
  const [persons, setPersons] = useState([]);
  const [inFlightDeals, setInFlightDeals] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedOwner, setExpandedOwner] = useState(null);
  const [expandedDeal, setExpandedDeal] = useState(null);

  const loadPayouts = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await fetchFromDentalAPI('/api/payouts');
      if (data?.success) {
        setPersons(data.data || []);
        setInFlightDeals(data.inFlightDeals || []);
      } else {
        setError(data?.error || 'Failed to load payouts');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { loadPayouts(); }, []);

  const totalDeals = persons.reduce((s, p) => s + p.deals.length, 0);
  const grandPaidOut = persons.reduce((s, p) => s + p.totalCommissionPaidOut, 0);
  const grandPayable = persons.reduce((s, p) => s + p.totalCommissionPayable, 0);
  const grandProjected = persons.reduce((s, p) => s + p.totalCommissionProjected, 0);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 600 }}>Payouts</h2>
          <p style={{ margin: '4px 0 0', fontSize: '14px', color: '#6b7280' }}>
            Commission tracking per partner / deal owner
          </p>
        </div>
        <button
          onClick={loadPayouts}
          disabled={isLoading}
          style={{
            padding: '8px 16px', backgroundColor: isLoading ? '#9ca3af' : '#059669',
            color: 'white', border: 'none', borderRadius: '8px',
            fontSize: '14px', fontWeight: 500, cursor: isLoading ? 'not-allowed' : 'pointer',
          }}
        >
          {isLoading ? 'Loading...' : 'Refresh'}
        </button>
      </div>

      {isLoading && (
        <div style={{ textAlign: 'center', padding: '60px 0', color: '#6b7280' }}>
          Reconciling deals...
        </div>
      )}

      {error && !isLoading && (
        <div style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '12px', padding: '16px' }}>
          <p style={{ margin: 0, fontWeight: 500, color: '#991b1b' }}>Error loading payouts</p>
          <p style={{ margin: '4px 0 0', fontSize: '14px', color: '#dc2626' }}>{error}</p>
        </div>
      )}

      {!isLoading && !error && (
        <>
          {/* Summary cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '24px' }}>
            <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '20px' }}>
              <p style={{ margin: 0, fontSize: '14px', color: '#6b7280' }}>Total Deals</p>
              <p style={{ margin: '4px 0 0', fontSize: '28px', fontWeight: 600 }}>{totalDeals}</p>
            </div>
            <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '20px' }}>
              <p style={{ margin: 0, fontSize: '14px', color: '#6b7280' }}>Paid Out</p>
              <p style={{ margin: '4px 0 0', fontSize: '28px', fontWeight: 600, color: '#059669' }}>{fmt(grandPaidOut)}</p>
              <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#9ca3af' }}>Commission already disbursed</p>
            </div>
            <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '20px' }}>
              <p style={{ margin: 0, fontSize: '14px', color: '#6b7280' }}>Payable</p>
              <p style={{ margin: '4px 0 0', fontSize: '28px', fontWeight: 600, color: '#b45309' }}>{fmt(grandPayable)}</p>
              <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#9ca3af' }}>Due on next payout date</p>
            </div>
            <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '20px' }}>
              <p style={{ margin: 0, fontSize: '14px', color: '#6b7280' }}>Projected (First Year)</p>
              <p style={{ margin: '4px 0 0', fontSize: '28px', fontWeight: 600 }}>{fmt(grandProjected)}</p>
              <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#9ca3af' }}>Full first-year projection</p>
            </div>
          </div>

          {/* Person cards */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {persons.map((person) => (
              <div key={person.name} style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: '12px', overflow: 'hidden' }}>
                <div
                  onClick={() => setExpandedOwner(expandedOwner === person.name ? null : person.name)}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 24px', cursor: 'pointer' }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{
                      width: '40px', height: '40px', borderRadius: '50%', backgroundColor: '#059669',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: 'white', fontWeight: 600, fontSize: '14px',
                    }}>
                      {person.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                    </div>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <p style={{ margin: 0, fontWeight: 500 }}>{person.name}</p>
                        <RateBadge rate={person.commissionRate} />
                        {person.source === 'partnership_source' && (
                          <span style={{
                            display: 'inline-flex', alignItems: 'center', padding: '2px 8px',
                            borderRadius: '9999px', fontSize: '12px', fontWeight: 500,
                            backgroundColor: '#f5f3ff', color: '#6d28d9',
                          }}>Partner</span>
                        )}
                      </div>
                      <p style={{ margin: 0, fontSize: '14px', color: '#6b7280' }}>
                        {person.deals.length} deal{person.deals.length !== 1 ? 's' : ''}
                      </p>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '32px' }}>
                    <div style={{ textAlign: 'right' }}>
                      <p style={{ margin: 0, fontSize: '13px', color: '#6b7280' }}>Paid Out</p>
                      <p style={{ margin: 0, fontWeight: 600, color: '#059669' }}>{fmt(person.totalCommissionPaidOut)}</p>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <p style={{ margin: 0, fontSize: '13px', color: '#6b7280' }}>Payable</p>
                      <p style={{ margin: 0, fontWeight: 600, color: '#b45309' }}>{fmt(person.totalCommissionPayable)}</p>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <p style={{ margin: 0, fontSize: '13px', color: '#6b7280' }}>Projected</p>
                      <p style={{ margin: 0, fontWeight: 500 }}>{fmt(person.totalCommissionProjected)}</p>
                    </div>
                    <span style={{ transform: expandedOwner === person.name ? 'rotate(180deg)' : '', transition: 'transform 0.2s' }}>{'\u25BC'}</span>
                  </div>
                </div>

                {expandedOwner === person.name && (
                  <div style={{ borderTop: '1px solid #f3f4f6' }}>
                    {person.deals.map((deal) => {
                      const paidCount = deal.journey.filter((m) => m.status !== 'future').length;
                      const isExpanded = expandedDeal === deal.dealRecordId;

                      return (
                        <div key={deal.dealRecordId} style={{ borderBottom: '1px solid #f3f4f6' }}>
                          <div
                            onClick={() => setExpandedDeal(isExpanded ? null : deal.dealRecordId)}
                            style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 24px', cursor: 'pointer' }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', minWidth: 0 }}>
                              <div style={{ minWidth: 0 }}>
                                <p style={{ margin: 0, fontWeight: 500, fontSize: '14px' }}>{deal.dealName}</p>
                                <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#6b7280' }}>{deal.companyName}</p>
                              </div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '20px', flexShrink: 0 }}>
                              <Badge label={deal.subscriptionStatus} colorMap={statusColors} />
                              <div style={{ fontSize: '13px', color: '#4b5563', width: '80px', textAlign: 'center' }}>
                                {fmtDate(deal.subscriptionStartDate)}
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', width: '64px' }}>
                                <ProgressBar current={paidCount} total={12} />
                                <span style={{ fontSize: '12px', color: '#6b7280', whiteSpace: 'nowrap' }}>
                                  {paidCount}/12
                                </span>
                              </div>
                              <div style={{ width: '80px', textAlign: 'right' }}>
                                <p style={{ margin: 0, fontSize: '12px', color: '#6b7280' }}>Paid Out</p>
                                <p style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: '#059669' }}>{fmt(deal.totalCommissionPaidOut)}</p>
                              </div>
                              <div style={{ width: '80px', textAlign: 'right' }}>
                                <p style={{ margin: 0, fontSize: '12px', color: '#6b7280' }}>Payable</p>
                                <p style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: '#b45309' }}>{fmt(deal.totalCommissionPayable)}</p>
                              </div>
                              <div style={{ width: '80px', textAlign: 'right' }}>
                                <p style={{ margin: 0, fontSize: '12px', color: '#6b7280' }}>Projected</p>
                                <p style={{ margin: 0, fontSize: '14px', fontWeight: 500 }}>{fmt(deal.totalCommissionProjected)}</p>
                              </div>
                              <span style={{ transform: isExpanded ? 'rotate(180deg)' : '', transition: 'transform 0.2s', fontSize: '12px', color: '#9ca3af' }}>{'\u25BC'}</span>
                            </div>
                          </div>

                          {isExpanded && (
                            <div style={{ padding: '4px 24px 20px' }}>
                              <JourneyTimeline journey={deal.journey} />
                              <div style={{ marginTop: '12px', display: 'flex', alignItems: 'center', gap: '16px', fontSize: '12px', color: '#9ca3af' }}>
                                <span>Start: {fmtDate(deal.subscriptionStartDate)}</span>
                                <span style={{ color: '#d1d5db' }}>|</span>
                                <span>First Year End: {fmtDate(deal.firstYearEnd)}</span>
                                <span style={{ color: '#d1d5db' }}>|</span>
                                <span>Month {Math.min(deal.currentMonth, 12)} of 12</span>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>

          {persons.length === 0 && (
            <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '48px', textAlign: 'center' }}>
              <p style={{ margin: 0, color: '#6b7280' }}>No payouts to display</p>
              <p style={{ margin: '4px 0 0', fontSize: '14px', color: '#9ca3af' }}>Closed Won deals with active subscriptions will appear here</p>
            </div>
          )}

          <InFlightSection deals={inFlightDeals} showRecipient={true} />
        </>
      )}
    </div>
  );
};

export default PayoutsTab;
